/**
 * The learner's own town as `GET /api/town` sends it (server/src/town.ts), and its adapter to the snapshot both
 * renderers draw. DOM-free, so the node tests run it.
 *
 * The server sends materials, never box numbers, and works out lamps, ivy, overgrowth, patina and hard-won itself
 * (v1 rules), so the client needs no dates. The adapter maps each material back to a stage for the renderers' own
 * use; nothing on screen shows it as a number.
 */
import type { LexEntry } from './building-lexicon';
import { townPropsOf } from './props';
import type { Kind, SnapItem, TownSnapshot } from './town-data';
import type { Opens } from '../types';
import type { Street } from './streets';

export const MATERIALS = ['fog', 'scaffolding', 'wood', 'stone', 'gold'] as const;
export type Material = (typeof MATERIALS)[number];

export interface TownBanner { raidId: string; band: 'breach' | 'sacked'; districtId: string; attacker: string; attackerId: string }

export interface OwnTownBuilding {
  key: string;
  /** Home district: the first moment the word was heard in. */
  district: string;
  /** The other moments it was heard in (each makes a road). */
  also: string[];
  kind: Kind;
  material: Material;
  condition?: 'maintained' | 'neglected' | 'unproven';
  /** Known or saved for later: no upkeep, so no condition. Absent for an active word. */
  status?: 'known' | 'later';
  lamp: boolean;
  glow: number;
  /** A lamp going out: the word was last passed 6–7 days ago (v1 ticket 15). */
  fading?: boolean;
  ivy: boolean;
  overgrowth: number;
  patina: number;
  hardWon: boolean;
  word: { text: string; meaning: string; latin?: string };
  /** A newcomer word heard since in one of the learner's recordings (v1 ticket 42). */
  wild?: true;
  /** A rude or vulgar word (v1 ticket 45). */
  rude?: 'mild' | 'strong';
  /** Cracked by a friend's raid (v1 ticket 22): who hit it (their name; show it in `<bdi dir="auto">`). Never a box. */
  cracked?: { by: string };
}

/** What a raid left on a district (v1 ticket 22): the repair bar (0 to 1, of the health it had), who hit it, a gate hit. */
export interface DistrictCracks { bar: number; by: string[]; gate?: true }

/**
 * The newcomers' offer (v1 ticket 42), one of two kinds. `arrive`: a situation, its title in the interface language,
 * where they come, and (the revamp) who comes. An older server sends no `kind` and no name: read it as `arrive`.
 * `waiting`: a newcomer from an earlier day whose question has no answer yet (1 to 3 days since).
 */
export type NewcomersOffer = NewcomersArrive | NewcomersWaiting;

/** Why no newcomer offer stands yet; actions are sent only when the server says the route has useful work. */
export type NewcomerReadiness =
  | { reason: 'need-word'; action: 'capture' }
  | { reason: 'fog-open'; action: 'round' }
  | { reason: 'fog-waits' }
  | { reason: 'landing'; action: 'landing'; momentId: string }
  | { reason: 'allowance' }
  | { reason: 'met-today' }
  | { reason: 'not-now' };
export interface NewcomersArrive {
  kind?: 'arrive';
  id: string;
  title: string;
  near: { momentId: string; title: string };
  name?: string;
  script?: string;
  gender?: 'female' | 'male';
}
export interface NewcomersWaiting {
  kind: 'waiting';
  momentId: string;
  name: string;
  script?: string;
  role?: string;
  /** The question in the target language; `questionLatin` its Finglish (a Persian target), shown first. */
  question: string;
  questionLatin?: string;
  translation: string;
  days: number;
}

/** A newcomer living in a district (the revamp): who, and whether their question still waits for an answer. */
export interface DistrictPerson { name: string; script?: string; waiting: boolean }

export interface OwnTown {
  /** The town's target language: it picks the style kit. */
  language: string;
  now: string;
  /**
   * `lanes`: its moment's lines; `paved`: how many of them the learner knows every word of (v1 ticket 31);
   * `streets`: the lines holding two or more of its own buildings, their keys in line order (streets.ts).
   * `imagined`: a typed moment's imagined scene (its landing sign says so; the town draws it like any other).
   * `newcomers`: a newcomer scene (v1 ticket 42; its landing says so).
   */
  /** An attacker's banner still flying after a breach (at the aimed district's gate) or a sack (over the square): the cracks of that raid are not all mended. Beta. */
  banners?: TownBanner[];
  districts: { id: string; title: string; createdAt: string; lanes?: number; paved?: number; streets?: Street[]; imagined?: true; shared?: true; newcomers?: true | DistrictPerson; answers?: { name: string; script?: string }; cracked?: DistrictCracks }[];
  buildings: OwnTownBuilding[];
  roads: { a: string; b: string; shared: string[] }[];
  answers: number;
  counts: Record<Material, number> & { lamps: number; ivy: number; roads: number; fading?: number };
  /** By day: every practised word is lit (server `dayOf`); absent from older servers. */
  day?: boolean;
  /**
   * The word lookups stored with the town's words (v1 ticket 13, from Wiktionary): parts of speech, gender, lemma,
   * one pronunciation, word family. Absent without an index; home-town.ts registers it for the grammar.
   */
  lexicon?: Record<string, LexEntry>;
  /**
   * The moments not landed yet, oldest first (v1 ticket 41): each lands once (`/land/<id>`), and home opens the
   * oldest. The server always sends it; the synthetic towns have none.
   */
  landing?: string[];
  /** The town's name (v1 ticket 40), absent until named: show it in `<bdi dir="auto">`, else "Your town". */
  name?: string;
  /** A name the learner's invitation carried (from the waitlist), while the town has none: the naming map starts with it typed in. Show it in `<bdi dir="auto">`. */
  suggestedName?: string;
  /** The night the town was founded (ISO): the stone's night, else the first moment's; absent with neither. */
  foundedAt?: string;
  /** The first stone is laid (named or "Name it later"). */
  stone?: true;
  /**
   * The first night's parts due now (v1 ticket 40): `opening` for a learner with no moment who has not seen it (home
   * opens `/opening`), `naming` while the town has no name and naming was never seen. Absent when none is due.
   */
  due?: { opening?: true; naming?: true };
  /** The newcomers' offer while it stands (v1 ticket 42): home's panel offers Meet them, Someone else, Not now. */
  newcomers?: NewcomersOffer;
  /** The current blocker and only an eligible recovery route, when no offer stands. */
  newcomerReadiness?: NewcomerReadiness;
  /** Today's newcomers were written and dropped (v1 ticket 42): home says so in one quiet line. */
  newcomersFailed?: true;
}

/**
 * A district's sheet (`GET /api/town/districts/:id`, v1 ticket 27): its counts in plain words, and how many first
 * looks its run would add now.
 */
export interface DistrictSheet {
  id: string;
  title: string;
  condition?: 'maintained' | 'mixed' | 'neglected' | 'unproven';
  counts: { ivy: number; dark: number; fog: number };
  firstLooks: number;
  /** What is left of its moment's first lesson today (v1 ticket 41), only while cards are left: Continue the first look. */
  firstLesson?: { left: number };
  /** A raid's damage still open here (v1 ticket 22): the repair bar and who hit it. */
  cracked?: DistrictCracks;
}

/**
 * A building's sheet (`GET /api/town/building?key=`): its word and line, its material (never a box), lamp and ivy,
 * and for a fog building whether its first look can be taken now or its district's are done for today.
 */
export interface BuildingSheet {
  key: string;
  district: string;
  also: string[];
  kind: Kind;
  material: Material;
  condition?: 'maintained' | 'neglected' | 'unproven';
  lamp: boolean;
  ivy: boolean;
  word: OwnTownBuilding['word'];
  line: { ca: string; latin?: string; en?: string } | null;
  /** `locked`: the climb holds its first look back until `opens` (v1 ticket 29). */
  firstLook?: 'now' | 'done' | 'locked';
  opens?: Opens;
  /** A newcomer word heard since in a recording (v1 ticket 42): the first such recording. */
  wild?: { momentId: string; title: string };
  /** A newcomer's word: the person who lives in its district, and whether their question still waits. */
  person?: DistrictPerson;
  /** A word taken from a friend's raid (v1 ticket 49): who left it. */
  trophy?: { from: string };
  /** Cracked by a friend's raid (v1 ticket 22): who hit it. */
  cracked?: { by: string };
  /** A rude or vulgar word (v1 ticket 45). */
  rude?: 'mild' | 'strong';
}

/**
 * The caption for a town or district with no upkeep to read: every word above the fog is known (`town.knownOnly`), or
 * some are saved for later (`town.restingOnly`). Null with no word above the fog, or while any active word needs review.
 */
export function restingCaption(buildings: readonly Pick<OwnTownBuilding, 'material' | 'status'>[]): 'town.knownOnly' | 'town.restingOnly' | null {
  const out = buildings.filter((b) => b.material !== 'fog');
  if (out.length === 0 || out.some((b) => b.status === undefined)) return null;
  return out.every((b) => b.status === 'known') ? 'town.knownOnly' : 'town.restingOnly';
}

/** The state line a building's sheet opens with: its condition, else its fog, a known or a saved-for-later line, then its lamp or dark lamp. */
export type SheetState = 'condition' | 'fog' | 'known' | 'later' | 'lamp' | 'dark';

export function sheetStateOf(b: Pick<OwnTownBuilding, 'material' | 'condition' | 'status' | 'lamp'>): SheetState {
  if (b.condition) return 'condition';
  if (b.material === 'fog') return 'fog';
  if (b.status) return b.status;
  return b.lamp ? 'lamp' : 'dark';
}

/** The server's town as a snapshot: buildings keep the server's order, and its lamps and ivy override the dates. */
export function snapshotOf(t: OwnTown): TownSnapshot {
  const born = new Map(t.districts.map((d) => [d.id, d.createdAt]));
  const items = t.buildings.map((b, seq): SnapItem => {
    const box = Math.max(0, MATERIALS.indexOf(b.material));
    return {
      key: b.key, ca: b.word.text, en: b.word.meaning, kind: b.kind, ...(b.word.latin ? { latin: b.word.latin } : {}),
      box, learned: box >= 3, attempts: 0, passes: 0, practisedAt: null, dueAt: t.now,
      firstSeenAt: born.get(b.district) ?? t.now, momentIds: [b.district, ...b.also], seq,
      lamp: b.lamp, glow: b.glow, ivy: b.ivy, overgrowth: b.overgrowth, patina: b.patina, hardWon: b.hardWon,
      cracked: !!b.cracked,
      ...(b.condition ? { condition: b.condition } : {}),
    };
  });
  return {
    id: 'own', label: 'Your town', language: t.language, now: t.now, moments: t.districts, items, answers: t.answers,
    props: townPropsOf(t),
  };
}

/**
 * The town as drawn, without the read's clock (`now`, and every item's `dueAt` with it): two reads a few seconds
 * apart of an unchanged town compare equal, so Done redraws only for a real change.
 */
export function sceneOf(own: OwnTown): string {
  const { now: _now, items, ...rest } = snapshotOf(own);
  return JSON.stringify({ ...rest, items: items.map(({ dueAt: _due, ...it }) => it) });
}

/** What a tended round changed in the town: word texts per change, in the round's order. */
export interface TownChanges {
  /** Ivy that went: a due word passed. */
  ivy: string[];
  /** Lamps that came on: a word passed with none in the last 7 days, or with its lamp going out. */
  lamps: string[];
  /** Buildings that climbed a material, out of the fog already. */
  climbed: string[];
  /** Words that left the fog. */
  fog: string[];
  /** Cracks repaired (v1 ticket 22): buildings cracked before and sound after, whichever word the round asked. */
  repaired: string[];
}

/**
 * The town before and after a tended round, compared for the round's words (v1 ticket 27): ivy cleared, lamps lit,
 * buildings climbed, fog lifted, each word as the learner reads it (a Persian one in Finglish). Materials only; a
 * box never shows.
 */
export function townChanges(before: OwnTown, after: OwnTown, keys: readonly string[]): TownChanges {
  const was = new Map(before.buildings.map((b) => [b.key, b]));
  const now = new Map(after.buildings.map((b) => [b.key, b]));
  const out: TownChanges = { ivy: [], lamps: [], climbed: [], fog: [], repaired: [] };
  for (const a of before.buildings) if (a.cracked && now.get(a.key) && !now.get(a.key)!.cracked) out.repaired.push(a.word.latin || a.word.text);
  for (const key of new Set(keys)) {
    const a = was.get(key), b = now.get(key);
    if (!a || !b) continue;
    const text = b.word.latin || b.word.text;
    if (a.ivy && !b.ivy) out.ivy.push(text);
    if ((!a.lamp || a.fading) && b.lamp && !b.fading) out.lamps.push(text);
    if (a.material === 'fog' && b.material !== 'fog') out.fog.push(text);
    else if (MATERIALS.indexOf(b.material) > MATERIALS.indexOf(a.material)) out.climbed.push(text);
  }
  return out;
}
